// ABOUTME: Compiles the find options (text or regex, match case, whole word) into the RegExp the server uses.
// ABOUTME: Mirrors the server compile so the preview diff and the "try it" box match the scan exactly.

export type FindPatternInput = {
    find: string;
    isRegex: boolean;
    caseSensitive: boolean;
    wholeWord: boolean;
};

export type FindPatternResult = { regex: RegExp } | { error: string };

/** Returned when there is nothing to search for; the UI disables the scan instead of showing it. */
export const EMPTY_FIND_ERROR = 'The find text is empty';

// Only the characters whose escape is legal under the `u` flag (`\-` for instance is a syntax error).
const SYNTAX = /[\\^$.*+?()[\]{}|/]/g;

export function buildFindPattern({
    find,
    isRegex,
    caseSensitive,
    wholeWord,
}: FindPatternInput): FindPatternResult {
    if (!find) return { error: EMPTY_FIND_ERROR };

    const escaped = isRegex ? find : find.replace(SYNTAX, '\\$&');
    // A word boundary that knows about accented letters: `\b` is ASCII-only even under `u`.
    const source = wholeWord ? `(?<![\\p{L}\\p{N}_])(?:${escaped})(?![\\p{L}\\p{N}_])` : escaped;
    const flags = `gu${caseSensitive ? '' : 'i'}`;

    try {
        return { regex: new RegExp(source, flags) };
    } catch (e) {
        return { error: e instanceof Error ? e.message : String(e) };
    }
}

export const isFindPatternError = (result: FindPatternResult): result is { error: string } =>
    'error' in result;

/** The compiled pattern, or `null` when it is empty or does not compile. */
export const findRegexOrNull = (input: FindPatternInput): RegExp | null => {
    const result = buildFindPattern(input);
    return isFindPatternError(result) ? null : result.regex;
};
